import type {
  FileContentResponse,
  FileVersion,
  SnapshotResult,
  UploadResult,
  Workspace,
  WorkspaceFileEntry,
} from './types';

/**
 * Client for the canonical workspace-store API (the PINNED contract). Every
 * call returns a discriminated result so the UI can degrade honestly: a 404
 * (sibling endpoints not landed) surfaces as `notAvailable`, distinct from a
 * real server error, distinct from a network failure.
 *
 * TODO(W1 sibling): endpoints under /api/workspaces/** are owned by the
 * canonical-store agent. Until they land, every call here resolves to
 * `notAvailable: true` and the UI shows a "storage not available yet" state.
 */

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; error: string; notAvailable: boolean };

async function request<T>(input: string, init?: RequestInit): Promise<ApiResult<T>> {
  let res: Response;
  try {
    res = await fetch(input, init);
  } catch (err) {
    return {
      ok: false,
      status: 0,
      error: err instanceof Error ? err.message : 'Network request failed',
      notAvailable: false,
    };
  }

  if (!res.ok) {
    // 404 = the endpoint (or workspace) does not exist yet. We treat that as
    // "storage not available" so the empty state can explain it, not crash.
    const notAvailable = res.status === 404;
    let error = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      error = body.error ?? body.message ?? error;
    } catch {
      /* non-JSON body — keep the status message */
    }
    return { ok: false, status: res.status, error, notAvailable };
  }

  const data = (await res.json()) as T;
  return { ok: true, data };
}

export function resolveWorkspace(sessionId: string): Promise<ApiResult<{ workspace: Workspace }>> {
  return request('/api/workspaces/resolve', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ kind: 'session', ownerRefId: sessionId }),
  });
}

export function listFiles(
  workspaceId: string,
): Promise<ApiResult<{ workspaceId: string; files: WorkspaceFileEntry[] }>> {
  return request(`/api/workspaces/${encodeURIComponent(workspaceId)}/files`);
}

export function getFileContent(
  workspaceId: string,
  path: string,
  version?: number,
): Promise<ApiResult<FileContentResponse>> {
  const params = new URLSearchParams({ path });
  if (version !== undefined) params.set('version', String(version));
  return request(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/files/content?${params.toString()}`,
  );
}

/**
 * Result of a text-editor save. Distinguishes an optimistic-concurrency conflict
 * (409 stale — the file moved under the editor) from ordinary failures so the
 * editor can offer reload-or-overwrite instead of a generic error.
 */
export type WriteTextResult =
  | { ok: true; data: UploadResult }
  | { ok: false; conflict: true; currentVersion: number; error: string }
  | { ok: false; conflict: false; status: number; error: string; notAvailable: boolean };

/**
 * Save text through the SAME canonical write path uploads + tools use
 * (POST /api/workspaces/:id/files, JSON body). `baseVersion` is the version the
 * editor loaded from — a stale write is refused with 409 and surfaced as a
 * conflict. Pass `baseVersion: undefined` to force-overwrite.
 */
export async function writeTextFile(
  workspaceId: string,
  path: string,
  textContent: string,
  mime?: string,
  baseVersion?: number,
): Promise<WriteTextResult> {
  let res: Response;
  try {
    res = await fetch(`/api/workspaces/${encodeURIComponent(workspaceId)}/files`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        path,
        textContent,
        ...(mime ? { mime } : {}),
        ...(baseVersion !== undefined ? { baseVersion } : {}),
      }),
    });
  } catch (err) {
    return {
      ok: false,
      conflict: false,
      status: 0,
      error: err instanceof Error ? err.message : 'Network request failed',
      notAvailable: false,
    };
  }

  if (res.status === 409) {
    let currentVersion = 0;
    let error = 'This file changed since you opened it.';
    try {
      const body = (await res.json()) as { currentVersion?: number; error?: string };
      if (typeof body.currentVersion === 'number') currentVersion = body.currentVersion;
      if (body.error) error = body.error;
    } catch {
      /* keep defaults */
    }
    return { ok: false, conflict: true, currentVersion, error };
  }

  if (!res.ok) {
    const notAvailable = res.status === 404;
    let error = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      error = body.error ?? body.message ?? error;
    } catch {
      /* non-JSON body */
    }
    return { ok: false, conflict: false, status: res.status, error, notAvailable };
  }

  const data = (await res.json()) as UploadResult;
  return { ok: true, data };
}

export function uploadFile(
  workspaceId: string,
  file: File,
  path: string,
): Promise<ApiResult<UploadResult>> {
  const form = new FormData();
  form.append('file', file);
  form.append('path', path);
  return request(`/api/workspaces/${encodeURIComponent(workspaceId)}/files`, {
    method: 'POST',
    body: form,
  });
}

export function deleteFile(
  workspaceId: string,
  path: string,
): Promise<ApiResult<{ deleted: boolean; path: string }>> {
  return request(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/files?path=${encodeURIComponent(path)}`,
    { method: 'DELETE' },
  );
}

export function getFileHistory(
  workspaceId: string,
  path: string,
): Promise<ApiResult<{ path: string; versions: FileVersion[] }>> {
  return request(
    `/api/workspaces/${encodeURIComponent(workspaceId)}/files/history?path=${encodeURIComponent(path)}`,
  );
}

export function snapshotFromSandbox(workspaceId: string): Promise<ApiResult<SnapshotResult>> {
  return request(`/api/workspaces/${encodeURIComponent(workspaceId)}/snapshot-from-sandbox`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({}),
  });
}
